import * as React from "react";
import * as moment from "moment-timezone";
import { toast } from "react-toastify";

import { InfoBar } from "src/library/components/InfoBar";
import { IInfoItem } from "src/library/components/InfoBar/InfoBarItem";
import { ProcessJobStatusDisplay } from "src/core/shared/components/ProcessJobStatus";
import { IProcessJob } from "src/core/process-job/api/processJobs.api";
import { Button } from "src/library/components/Button";
import { useS3Api } from "src/core/shared/components/S3/s3.api";

interface IProps {
  isLoading: boolean;
  data: IProcessJob | null;
}

export const ProcessJobSidebar = ({ isLoading, data }: IProps) => {
  const [ listS3LinksRes, listS3Links ] = useS3Api("LIST_PRESIGNED_URLS");
  const [ fileNames, setFileNames ] = React.useState<string[]>([]);

  /**
   * For getting multiple s3 download Links
   */
  const getMultipleFileLinks = React.useCallback(
    (s3Keys: string[]) => {
      const encodedKeys: string[] = [];
      const names: string[] = [];

      for (const s3Key of s3Keys) {
        //store the file names at the same index in a different state array.
        const name = s3Key.split("/").at(-1);

        if (name) {
          names.push(name);
        }

        encodedKeys.push(encodeURIComponent(s3Key));
      }

      setFileNames(names);
      listS3Links({ keys: encodedKeys });
    }, [ listS3Links ]);

  /**
    * Download A file - This can process multiple files in a loop.
    */
  const downloadFile = React.useCallback(async (url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");

      link.target = "_blank";
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Error downloading file: " + fileName);
    }
  }, []);

  React.useEffect(() => {
    if (listS3LinksRes.data?.signedUrls) {
      listS3LinksRes.data.signedUrls.map((link, i) => {
        downloadFile(link, fileNames[ i ]);
      });
    }
  },
  [
    listS3LinksRes,
    downloadFile,
    fileNames
  ]);

  const items = React.useMemo(() => {
    let dataItems: IInfoItem[] = [];

    if (data) {
      dataItems = [
        {
          label: "Name",
          value: data.name
        },
        {
          label: "Status",
          value: <ProcessJobStatusDisplay status={data.status} />
        },
        {
          label: "Details",
          value: data.statusMessage
        },
        {
          label: "Total Created",
          value: typeof data.totalCreated === "number" ? data.totalCreated : "-"
        },
        {
          label: "Total Updated",
          value: typeof data.totalUpdated === "number" ? data.totalUpdated : "-"
        },
        {
          label: "Started At",
          value: data.startedAt ?
            moment
              .utc(data.startedAt)
              .local()
              .format("HH:mm DD MMMM YYYY") :
            "Not started"
        },
        {
          label: "Finished At",
          value: data.startedAt ?
            moment
              .utc(data.finishedAt)
              .local()
              .format("HH:mm DD MMMM YYYY") :
            "Not finished"
        },
        {
          label: "Triggered By",
          value:
            data.triggeredByType === "system" ?
              data.triggeredByType :
              data.triggeredByUser ?
                `${data.triggeredByUser.firstName} (${data.triggeredByUser.email})` :
                "User"
        },
        {
          label: "Created",
          value: data.addedAt ?
            moment
              .utc(data.addedAt)
              .local()
              .format("HH:mm DD MMMM YYYY") :
            "Not started"
        }
      ];

      if (data.autoRetry) {
        dataItems.unshift({
          label: "Auto Retry At",
          value: moment
            .utc(data.autoRetry)
            .local()
            .format("HH:mm DD MMMM YYYY")
        });
      }
    }

    if (data?.documents?.length) {
      const s3Keys = data?.documents?.map(d => d.key);

      for (const doc of data.documents) {
        dataItems.push({
          label: "Filename",
          value: doc.slug
        });
      }

      if (s3Keys?.length) {
        dataItems.push({
          label: `${s3Keys.length > 1 ? "Files" : "File"}`,
          value: (
            <Button
              onClick={() => getMultipleFileLinks(s3Keys)}
            >
              {`${s3Keys.length > 1 ? "Download Files" : "Download File"}`}
            </Button>
          )
        });
      }
      //TODO All files should come from documents
    } else if (data?.jobData.s3Key) {
      dataItems.push({
        label: "File",
        value: (
          <Button
            onClick={() => getMultipleFileLinks([ data?.jobData.s3Key ])}
          >
            Download File
          </Button>)

      });
    }

    return dataItems || [];
  }, [ data, getMultipleFileLinks ]);

  return (
    <InfoBar
      isLoading={isLoading}
      title="Job details"
      items={items}
    />
  );
};
